// Lib
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

// Hooks
import useUser from '../../../../hooks/useUser';
import useUI from '../../../../hooks/useUI';
import useUnavailableForOrderProduct from '../../../../hooks/useUnavailableForOrderProduct';

// Utils
import { getProductTotalItemsOrdered } from '../../../../pages/OrderHistory/utils/getQuantity';

// Selectors
import { productsObjSelector } from '../../../../store/selectors';

// Styles
import { useStyles } from './OrderProductDetailsStyles';

const getPiRequiredText = (perUnitQty, piRequired) => {
  if (!perUnitQty) {
    return 'N/A';
  }
  return piRequired ? 'Yes' : 'No';
};

const getShippedToText = (address) =>
  `${address?.customer ? address.customer + ',' : ''} ${
    address?.full_address || ''
  }`;

const useOrderProductDetails = ({
  productId,
  order,
  orderItem,
  addressesCount,
}) => {
  const product = useSelector(
    (state) => productsObjSelector(state)?.[productId],
  );

  const { isUnavailableForOrder, unavailableLabel, unavailableWatermark } =
    useUnavailableForOrderProduct(product, false, true);

  const theme = useTheme();
  const { isKiteUser } = useUser();
  const { isMobileMode, isTabletMode, isDesktopMode } = useUI();
  const { classes, cx } = useStyles({
    isKiteUser,
    isUnavailableForOrder,
    isTabletMode,
    isMobileMode,
    isDesktopMode,
  });

  const totalOrdered = useMemo(
    () => getProductTotalItemsOrdered(product, orderItem, addressesCount),
    [product, orderItem, addressesCount],
  );

  return {
    product,
    isUnavailableForOrder,
    unavailableLabel,
    unavailableWatermark,
    theme,
    isMobileMode,
    isTabletMode,
    isDesktopMode,
    classes,
    cx,
    totalOrdered,
    piRequiredText: getPiRequiredText(
      product?.prepack_quantity,
      product?.pi_required,
    ),
    shippedToText: getShippedToText(order.address),
  };
};

export default useOrderProductDetails;
